import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ClipboardCheck, Anchor, ChevronUp, Box, Map as MapIcon, Info, CloudRain } from 'lucide-react';
import { Ship } from '../types/ship';
import { Vessel } from '../types/vessel';
import { apiClient } from '../api/client';
import VesselProfileDrawer from '../components/vessel/VesselProfileDrawer';
import LiveAISPanel from '../components/map/LiveAISPanel';
import PortInfoPanel from '../components/map/PortInfoPanel';
import { useHasRole } from '../context/AuthContext';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

type RiskFilter = 'all' | 'high' | 'critical';

const VESSEL_TYPES = ['All', 'Cargo', 'Tanker', 'Fishing', 'Passenger'];

/** Bucket a free-text vessel-type into one of five canonical kinds we
 *  render distinctly on the map. The data layer hands us strings like
 *  "Crude Oil Tanker", "Fishing Vessel", "Container Ship" — we collapse
 *  to {tanker, cargo, container, fishing, passenger, other} so the
 *  symbol layer can pick the right icon + colour per vessel. */
function vesselKind(typeRaw: string | undefined): string {
  const t = (typeRaw ?? '').toLowerCase();
  if (!t) return 'other';
  if (t.includes('tanker') || t.includes('chem') || t.includes('lpg') || t.includes('lng') || t.includes('gas')) return 'tanker';
  if (t.includes('container'))                                     return 'container';
  if (t.includes('passenger') || t.includes('cruise') || t.includes('ferry') || t.includes('roro')) return 'passenger';
  if (t.includes('fish'))                                          return 'fishing';
  if (t.includes('cargo') || t.includes('bulk') || t.includes('general')) return 'cargo';
  return 'other';
}

/* Each vessel kind gets a one-character mark that Mapbox renders inside
 * the dot. Plain letters proved more readable than geometric symbols:
 *   T = Tanker, C = Cargo, B = Box (container), P = Passenger, F = Fishing
 *   ● = Other (kind we couldn't bucket — usually unknown AIS type)
 * Letters render identically across every platform and are immediately
 * legible at the small sizes the map uses. The colour-coded dot under
 * the letter carries the redundant signal so colour-blind users still
 * have the letter to read. */
const KIND_GLYPH: Record<string, string> = {
  tanker:    'T',
  cargo:     'C',
  container: 'B',
  passenger: 'P',
  fishing:   'F',
  other:     '·',
};

/** Tiny HTML escape — used for hover-popup interpolation. We can't
 *  use React inside Mapbox popups, so we go raw with a guarded escape
 *  to keep vessel names safe even if the AIS stream returns weirdness. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}

function shipsToGeoJSON(ships: Ship[]) {
  return {
    type: 'FeatureCollection' as const,
    features: ships.map((ship) => {
      const kind = vesselKind(ship.type);
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [ship.location.longitude, ship.location.latitude],
        },
        properties: {
          mmsi: ship.mmsi,
          name: ship.name,
          type: ship.type,
          kind,
          glyph: KIND_GLYPH[kind] ?? KIND_GLYPH.other,
          speed: ship.speed ?? 0,
          course: ship.course ?? 0,
          destination: ship.destination ?? '',
          risk_band: 'low', // default for live AIS ships — no risk score computed yet
        },
      };
    }),
  };
}

export default function MapPage() {
  const navigate = useNavigate();
  const canAccessInspections = useHasRole('inspector', 'admin');
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  // mapReady is **state** (not a ref) so the updateSource effect re-runs
  // when the map finishes loading. The previous ref-based design had a
  // race: when the ship-fetch resolved BEFORE map.on('load'), the load
  // handler called updateSource() with a STALE closure that captured an
  // empty ships array — and the [updateSource]-keyed effect didn't fire
  // again because updateSource hadn't changed. Result: ships were
  // fetched but the map painted empty until a filter click forced a
  // re-render. State + a [mapReady, updateSource] dep array fixes both
  // orderings: ships-arrive-first OR map-loads-first now both end with
  // the geojson source populated.
  const [mapReady, setMapReady] = useState(false);
  const mapLoaded = useRef(false); // kept for in-flight reads inside callbacks

  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);

  // ── Environmental overlays ────────────────────────────────────────────
  // Weather radar: RainViewer's free composite (attribution required, no
  // key). Frames update ~10-minutely; we fetch the latest frame path on
  // toggle-on. Seamarks: OpenSeaMap's buoyage/lights overlay (ODbL).
  // Both are raster layers slotted BENEATH the vessel layers so ships
  // always stay on top.
  const [radarOn, setRadarOn] = useState(false);
  const [seamarksOn, setSeamarksOn] = useState(false);

  function firstVesselLayerId(map: any): string | undefined { // eslint-disable-line @typescript-eslint/no-explicit-any
    return map.getStyle()?.layers?.find((l: { id: string }) => l.id.startsWith('vessels'))?.id;
  }

  async function toggleRadar() {
    const map = mapRef.current;
    if (!map) return;
    if (radarOn) {
      try { map.removeLayer('wx-radar'); map.removeSource('wx-radar'); } catch { /* absent */ }
      setRadarOn(false);
      return;
    }
    try {
      const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
      const j = await res.json();
      const frame = j?.radar?.past?.slice(-1)?.[0];
      if (!frame?.path) return;
      map.addSource('wx-radar', {
        type: 'raster',
        tiles: [`${j.host}${frame.path}/256/{z}/{x}/{y}/2/1_1.png`],
        tileSize: 256,
        attribution: '<a href="https://www.rainviewer.com/">RainViewer</a>',
      });
      map.addLayer(
        { id: 'wx-radar', type: 'raster', source: 'wx-radar', paint: { 'raster-opacity': 0.7 } },
        firstVesselLayerId(map),
      );
      setRadarOn(true);
    } catch { /* radar unavailable — leave toggle off */ }
  }

  function toggleSeamarks() {
    const map = mapRef.current;
    if (!map) return;
    if (seamarksOn) {
      try { map.removeLayer('seamarks'); map.removeSource('seamarks'); } catch { /* absent */ }
      setSeamarksOn(false);
      return;
    }
    try {
      map.addSource('seamarks', {
        type: 'raster',
        tiles: ['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '<a href="https://www.openseamap.org/">OpenSeaMap</a>',
      });
      map.addLayer(
        { id: 'seamarks', type: 'raster', source: 'seamarks' },
        firstVesselLayerId(map),
      );
      setSeamarksOn(true);
    } catch { /* already added */ }
  }
  const [ships, setShips] = useState<Ship[]>([]);
  // Freshness envelope from /api/ships — used to render the staleness
  // badge in the bottom-left of the map. Null until the first fetch
  // resolves; warning is non-null when we got mock fallback data.
  const [aisMeta, setAisMeta] = useState<{
    source: string;
    newest_position_age_sec: number | null;
    warning?: string | null;
  } | null>(null);
  // The bottom-left badge can be expanded into a live dashboard panel
  // (LiveAISPanel). Hint state: a one-time pulse on the chevron the
  // first time we land on the page so the inspector notices the badge
  // is interactive — then it quiets down.
  const [aisPanelOpen, setAisPanelOpen] = useState(false);
  // Zoom-aware tools — both the 3D toggle and the port-info panel are
  // useless when the inspector is looking at the whole hemisphere, so
  // we hide the tooblar until they've zoomed into a port-scale view
  // (zoom > 11 — ports become legible at this level on dark-v11).
  const [currentZoom, setCurrentZoom] = useState(1.2);
  const [is3D, setIs3D] = useState(false);
  const [portInfoOpen, setPortInfoOpen] = useState(false);
  const [portInfoCenter, setPortInfoCenter] = useState<{ lng: number; lat: number } | null>(null);
  const [hintShown, setHintShown] = useState(() => {
    try { return localStorage.getItem('varuna.ais.hint.shown') === '1'; } catch { return true; }
  });
  useEffect(() => {
    if (hintShown) return;
    const t = setTimeout(() => {
      try { localStorage.setItem('varuna.ais.hint.shown', '1'); } catch { /* ignore */ }
      setHintShown(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [hintShown]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');

  // Derived: apply filters before pushing to map.
  //
  // The default state ("All" type + "all" risk) MUST show every fetched
  // ship — there's no implicit hiding. Risk filter currently only hides
  // ships when the user explicitly picks "high" or "critical" (live AIS
  // ships default to risk_band='low' until risk scoring runs).
  const filteredShips = ships.filter((ship) => {
    const typeMatch =
      filterType === 'All' ||
      ship.type?.toLowerCase().includes(filterType.toLowerCase());
    const riskMatch =
      riskFilter === 'all' ||
      (ship as unknown as { risk_band?: string }).risk_band === riskFilter;
    return typeMatch && riskMatch;
  });

  // ── Polling ──────────────────────────────────────────────────────────────────
  const hasFitBounds = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchShips = async () => {
      try {
        const meta = await apiClient.getShipsWithMeta();
        const data = meta.data ?? [];
        if (!cancelled) {
          setShips(data);
          setAisMeta({
            source: meta.source,
            newest_position_age_sec: meta.newest_position_age_sec ?? null,
            warning: meta.warning ?? null,
          });
          // On first load, fly to the centroid of all ships
          if (!hasFitBounds.current && data.length > 0 && mapRef.current && mapLoaded.current) {
            hasFitBounds.current = true;
            // Ships without a position (fresh mock rows, stale feed gaps)
            // must not poison the centroid — NaN center makes flyTo throw.
            const lats = data.map((s) => s.location?.latitude).filter((v): v is number => Number.isFinite(v));
            const lngs = data.map((s) => s.location?.longitude).filter((v): v is number => Number.isFinite(v));
            if (lats.length === 0 || lngs.length === 0) return;
            const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
            const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
            // Cinematic descent from globe → operational view, easing out into
// the active AIS region. Slightly tilted final pitch keeps the world
// feeling 3D after the projection settles. Long duration is
// intentional — it sells "the platform is finding the fleet".
mapRef.current.flyTo({
  center: [avgLng, avgLat],
  zoom: 4.2,
  pitch: 12,
  bearing: 0,
  speed: 0.6,
  curve: 1.4,
  duration: 3200,
  essential: true,
});
          }
        }
      } catch (err) {
        console.error('Failed to fetch ships:', err);
      }
    };

    fetchShips();
    const interval = setInterval(fetchShips, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // ── Update GeoJSON source when filteredShips changes ────────────────────────
  const updateSource = useCallback(() => {
    if (!mapRef.current || !mapLoaded.current) return;
    const source = mapRef.current.getSource('vessels');
    if (source) {
      source.setData(shipsToGeoJSON(filteredShips));
    }
  }, [filteredShips]);

  useEffect(() => {
    if (!mapReady) return;
    updateSource();
  }, [mapReady, updateSource]);

  // ── Map initialisation ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainer.current) return;
    if (!MAPBOX_TOKEN) {
      console.error('REACT_APP_MAPBOX_TOKEN is not set');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mapboxgl = require('mapbox-gl');
    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Open on a slow-rotating globe so the very first frame after sign-in
    // is visually alive — then ease into mercator at the chosen centroid
    // once the AIS feed lands. Globe projection is a Mapbox 3.x feature;
    // the easeTo to mercator is animated via the projection-transition
    // helper below. We deliberately start with a wide view and a slight
    // tilt so the camera move feels like descent rather than a jump.
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      projection: 'globe',
      center: [12, 28],     // Mediterranean / Suez area — high vessel density
      zoom: 1.2,
      pitch: 25,
      bearing: -8,
      attributionControl: false,
    });
    mapRef.current = map;
    // Add attribution back as a compact control so the canvas isn't busy.
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    // Slow auto-rotation while we wait for ships to land. Cancelled the
    // moment fitBounds runs.
    let rotation = map.getBearing();
    const spinHandle = window.setInterval(() => {
      if (hasFitBounds.current) return;
      rotation = (rotation + 0.1) % 360;
      try {
        map.setBearing(rotation);
      } catch {
        /* map disposed */
      }
    }, 60);
    map.once('idle', () => {
      // First frame painted — kick the cinematic descent toward the
      // active vessels area. If ships haven't arrived yet the second
      // flyTo (in the load handler / fetch effect) will retarget.
      try {
        map.easeTo({
          center: [10, 35],
          zoom: 2.4,
          pitch: 18,
          bearing: 0,
          duration: 2200,
          essential: true,
        });
      } catch {
        /* ignore */
      }
    });
    // Stop auto-rotation on disposal.
    map.once('remove', () => clearInterval(spinHandle));
    // Track zoom so the toolbar can show / hide its zoom-aware tools.
    map.on('zoomend', () => {
      try { setCurrentZoom(map.getZoom()); } catch { /* disposed */ }
    });

    map.on('load', () => {
      mapLoaded.current = true;
      setMapReady(true);  // re-renders → updateSource effect fires with current ships

      // ── GeoJSON source with clustering ─────────────────────────────────────
      map.addSource('vessels', {
        type: 'geojson',
        data: shipsToGeoJSON([]),
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      });

      // Cluster circles
      map.addLayer({
        id: 'vessel-clusters',
        type: 'circle',
        source: 'vessels',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#ffffff',
          'circle-opacity': 0.9,
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18,   // default
            10, 24,
            50, 30,
            200, 38,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#93c5fd',
          'circle-stroke-opacity': 0.7,
        },
      });

      // Cluster count labels
      map.addLayer({
        id: 'vessel-count',
        type: 'symbol',
        source: 'vessels',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: {
          'text-color': '#1e293b',
        },
      });

      // Individual vessel points. Colour communicates the vessel KIND
      // (tanker / cargo / container / passenger / fishing) so an
      // inspector can read the kind of fleet at a glance from the map.
      // Risk band is conveyed separately by the glyph layer's halo
      // when a non-low band is present.
      map.addLayer({
        id: 'vessel-points',
        type: 'circle',
        source: 'vessels',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'kind'],
            'tanker',    '#06B6D4',  // cyan — brand
            'cargo',     '#10B981',  // emerald
            'container', '#3B82F6',  // blue
            'passenger', '#F59E0B',  // amber
            'fishing',   '#A78BFA',  // violet
            /* default */ '#94A3B8', // slate
          ],
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            3, 5,
            6, 8,
            10, 11,
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.92)',
          'circle-opacity': 0.95,
        },
      });

      // Glyph symbol layer: a single letter on top of the dot. Bold +
      // halo'd so it stays readable on any underlying basemap colour.
      // Hidden at low zooms where the dot is too small to fit a letter.
      map.addLayer({
        id: 'vessel-glyphs',
        type: 'symbol',
        source: 'vessels',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': ['get', 'glyph'],
          'text-font': ['DIN Pro Bold', 'Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': [
            'interpolate', ['linear'], ['zoom'],
            3, 0,    // hide at low zooms — circles too small
            5, 9,
            8, 11,
            12, 13,
          ],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#0B1220',
          'text-halo-color': 'rgba(255,255,255,0.85)',
          'text-halo-width': 1.2,
        },
      });

      // Hover popup — small branded card showing name + type + speed +
      // course. Single instance reused across hover events so we never
      // leak. Declared BEFORE the click handler so the handler's
      // closure captures it without a TDZ surprise.
      const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: 'varuna-hover-popup',
        anchor: 'top',
      });

      // ── Click: individual vessel ──────────────────────────────────────────
      // Two cleanups before opening the drawer so the handoff feels
      // crisp:
      //   1. Stop the click propagating up to the map's generic
      //      click handler (Mapbox docs: click events bubble unless
      //      explicitly stopped via originalEvent).
      //   2. Hide the hover popup so its mini-card doesn't briefly
      //      flash on top of the drawer as the cursor moves into the
      //      drawer's slide-in zone.
      map.on('click', 'vessel-points', (e: any) => {
        const feature = e.features?.[0];
        if (!feature) return;
        e.originalEvent?.stopPropagation?.();
        try { hoverPopup.remove(); } catch { /* not mounted */ }
        const mmsi = feature.properties?.mmsi;
        if (mmsi) {
          setProfileId(String(mmsi));
          setProfileOpen(true);
        }
      });

      // ── Click: cluster — zoom in ──────────────────────────────────────────
      map.on('click', 'vessel-clusters', (e: any) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource('vessels') as any;
        source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
          if (err) return;
          map.easeTo({
            center: (feature.geometry as any).coordinates,
            zoom: zoom + 0.5,
          });
        });
      });

      function fmtKind(k: string) {
        return k === 'other' ? 'Vessel' : k.charAt(0).toUpperCase() + k.slice(1);
      }

      map.on('mouseenter', 'vessel-points', (e: any) => {
        map.getCanvas().style.cursor = 'pointer';
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties ?? {};
        const speed = Number(p.speed ?? 0);
        // The AIS DESTINATION field is often a UN/LOCODE (5-char port
        // identifier — see components/shared/LocodeBadge). When it
        // matches the format, surface it as a small chip with full
        // tooltip; otherwise show as free-text.
        const dest = String(p.destination ?? '').trim();
        const locode = (() => {
          if (!dest) return null;
          for (const tok of dest.toUpperCase().split(/[\s>,/-]+/).filter(Boolean)) {
            if (/^[A-Z]{2}[A-Z0-9]{3}$/.test(tok)) return tok;
          }
          return null;
        })();
        const destHtml = locode
          ? `<span style="font-family:ui-monospace,monospace; font-size:10px; padding:1px 5px; border-radius:4px; background:rgba(59,130,246,0.10); border:1px solid rgba(59,130,246,0.30); color:#93C5FD" title="UN/LOCODE: ${escapeHtml(locode)}">${escapeHtml(locode)}</span>`
          : dest
          ? `<span style="color:#CBD5E1">${escapeHtml(dest.length > 24 ? dest.slice(0, 24) + '…' : dest)}</span>`
          : '';
        const html = `
          <div style="
            min-width:200px; padding:8px 10px;
            background: rgba(11,18,32,0.96);
            border: 1px solid rgba(6,182,212,0.35);
            border-radius: 10px;
            color: #F8FAFC;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 12px;
            line-height: 1.35;
            box-shadow: 0 12px 30px -10px rgba(0,0,0,0.6);
          ">
            <div style="display:flex; align-items:center; gap:6px; font-weight:700; color:#FFFFFF;">
              <span style="color:#06B6D4">${escapeHtml(String(p.glyph ?? '●'))}</span>
              <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:220px;">
                ${escapeHtml(String(p.name ?? 'Vessel'))}
              </span>
            </div>
            <div style="margin-top:3px; color:#94A3B8; font-size:11px;">
              ${escapeHtml(String(p.type ?? fmtKind(String(p.kind ?? 'other'))))}
              ${p.mmsi ? `· <span style="font-family:ui-monospace,monospace">MMSI ${escapeHtml(String(p.mmsi))}</span>` : ''}
            </div>
            <div style="margin-top:6px; display:flex; gap:10px; align-items:center; font-family:ui-monospace,monospace; font-size:11px; color:#CBD5E1;">
              <span>${speed.toFixed(1)} kn</span>
              <span style="color:#475569">·</span>
              <span>${Math.round(Number(p.course ?? 0))}°</span>
              ${destHtml ? `<span style="color:#475569">·</span> <span style="font-family:inherit; color:#94A3B8">→</span> ${destHtml}` : ''}
            </div>
            <div style="margin-top:6px; font-size:10px; color:#64748B;">Click for full profile</div>
          </div>`;
        hoverPopup.setLngLat(f.geometry.coordinates.slice()).setHTML(html).addTo(map);
      });
      map.on('mouseleave', 'vessel-points', () => {
        map.getCanvas().style.cursor = '';
        hoverPopup.remove();
      });
      map.on('mouseenter', 'vessel-clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'vessel-clusters', () => { map.getCanvas().style.cursor = ''; });

      // Populate with whatever ships loaded while the map was initialising
      updateSource();

      // Auto-fit to ships if already loaded
      if (!hasFitBounds.current && ships.length > 0) {
        hasFitBounds.current = true;
        const lats = ships.map((s) => s.location.latitude).filter(Boolean);
        const lngs = ships.map((s) => s.location.longitude).filter(Boolean);
        const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
        const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
        map.flyTo({
          center: [avgLng, avgLat],
          zoom: 4.2,
          pitch: 12,
          bearing: 0,
          speed: 0.6,
          curve: 1.4,
          duration: 3200,
          essential: true,
        });
      }
    });

    return () => {
      mapLoaded.current = false;
      setMapReady(false);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Switch between the operational 2D dark map and a realistic 3D
   * port view. The 3D mode swaps to satellite-streets-v12, layers
   * a DEM terrain source for hill shading, adds building extrusions,
   * and tilts the camera so the inspector gets a physical sense of
   * the port before they travel. Toggling back restores the dark
   * basemap with a flat top-down view. Mapbox is the sole renderer.
   */
  function toggle3D() {
    const map = mapRef.current;
    if (!map) return;
    const next = !is3D;
    setIs3D(next);
    if (next) {
      map.setStyle('mapbox://styles/mapbox/satellite-streets-v12');
      map.once('style.load', () => {
        try {
          if (!map.getSource('mapbox-dem')) {
            map.addSource('mapbox-dem', {
              type: 'raster-dem',
              url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
              tileSize: 512,
              maxzoom: 14,
            });
          }
          map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.4 });
          if (!map.getLayer('3d-buildings')) {
            map.addLayer({
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 13,
              paint: {
                'fill-extrusion-color': '#94A3B8',
                'fill-extrusion-height': ['get', 'height'],
                'fill-extrusion-base':   ['get', 'min_height'],
                'fill-extrusion-opacity': 0.7,
              },
            });
          }
          // Re-add the vessel source + layers — setStyle wipes them.
          if (!map.getSource('vessels')) {
            map.addSource('vessels', {
              type: 'geojson',
              data: shipsToGeoJSON(filteredShips),
              cluster: true,
              clusterMaxZoom: 12,
              clusterRadius: 50,
            });
            map.addLayer({
              id: 'vessel-points',
              type: 'circle',
              source: 'vessels',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': [
                  'match', ['get', 'kind'],
                  'tanker',    '#06B6D4',
                  'cargo',     '#10B981',
                  'container', '#3B82F6',
                  'passenger', '#F59E0B',
                  'fishing',   '#A78BFA',
                  '#94A3B8',
                ],
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': 'rgba(255,255,255,0.95)',
              },
            });
          }
          map.easeTo({ pitch: 60, bearing: -22, duration: 1500 });
        } catch (err) {
          console.warn('3D mode setup failed', err);
        }
      });
    } else {
      map.setStyle('mapbox://styles/mapbox/dark-v11');
      map.once('style.load', () => {
        try {
          map.setTerrain(null);
          map.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
        } catch { /* ignore */ }
      });
    }
  }

  /** Open the port-info side panel for the current map center. */
  function openPortInfo() {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    setPortInfoCenter({ lng: c.lng, lat: c.lat });
    setPortInfoOpen(true);
  }

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/vessels?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    // h-full (not h-screen): AppShell pins the map column to the viewport
    // under the global top bar, so full bleed is preserved without overflow.
    <div className="relative h-full min-h-[480px] w-full overflow-hidden bg-slate-950">
      {/* Map container */}
      <div ref={mapContainer} className="absolute inset-0" />

      {/* Missing token warning */}
      {!MAPBOX_TOKEN && (
        <div className="absolute inset-0 flex items-center justify-center z-30">
          <div className="bg-red-900/90 text-red-200 px-6 py-4 rounded-lg text-sm">
            REACT_APP_MAPBOX_TOKEN is not set — map cannot render.
          </div>
        </div>
      )}

      {/* ── Top bar ──────────────────────────────────────────────────────────
          Brand + user identity live in the global AppShell sidebar now; this
          bar keeps only the map's own controls (search, filters, actions). */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-3 px-4 py-2.5 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/60">
        {/* Search */}
        <div className="flex-1 max-w-xs">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search vessels…"
            className="w-full px-3 py-1.5 rounded-lg bg-slate-800/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-colors"
          />
        </div>

        {/* Type filter chips */}
        <div className="hidden lg:flex items-center gap-1 shrink-0">
          {VESSEL_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setFilterType(t)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                filterType === t
                  ? 'bg-cyan-600 text-white shadow-sm shadow-cyan-500/30'
                  : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Risk filter */}
        <div className="hidden md:flex items-center gap-1 shrink-0">
          <button
            onClick={() => setRiskFilter('all')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              riskFilter === 'all'
                ? 'bg-slate-600 text-white'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            All risk
          </button>
          <button
            onClick={() => setRiskFilter('high')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              riskFilter === 'high'
                ? 'bg-orange-600 text-white shadow-sm shadow-orange-500/30'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            High
          </button>
          <button
            onClick={() => setRiskFilter('critical')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              riskFilter === 'critical'
                ? 'bg-red-700 text-white shadow-sm shadow-red-500/30'
                : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            Critical
          </button>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Permanent demo link — visible to logged-out and logged-in users
            alike. Sales-friendly entry point that any inspector can share
            with a colleague: "go to varunaiq.com/vetting/demo". */}
        <button
          onClick={() => navigate('/vetting/demo')}
          title="See a 5-minute interactive walkthrough using sample vessel data"
          className="px-3 py-2 min-h-[40px] rounded-lg bg-slate-900/70 hover:bg-slate-800 border border-slate-700 hover:border-cyan-500/40 text-slate-200 hover:text-white text-xs font-medium transition-all shrink-0 active:scale-[0.98] inline-flex items-center gap-1.5"
        >
          <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
          <span className="hidden sm:inline">Demo</span>
        </button>

        {/* Inspector primary CTA — replaces the previous tiny top-right
            "Inspections" pill with a large, two-line, role-aware action.
            Inspectors land here as their primary workflow entry point;
            non-inspectors see the Vetting workbench landing instead. */}
        {canAccessInspections ? (
          <button
            onClick={() => navigate('/workspace')}
            title="Open your inspector workspace — open work, recent activity, insights"
            className="px-4 py-2 min-h-[44px] rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-bold shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-150 active:scale-[0.98] inline-flex items-center gap-2 shrink-0"
          >
            <ClipboardCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Inspect a vessel</span>
            <span className="sm:hidden">Inspect</span>
          </button>
        ) : (
          <button
            onClick={() => navigate('/vessels')}
            title="Browse vessel intelligence profiles"
            className="px-4 py-2 min-h-[44px] rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-cyan-500/40 hover:border-cyan-400 text-cyan-200 hover:text-white text-sm font-semibold transition-all duration-150 active:scale-[0.98] inline-flex items-center gap-2 shrink-0"
          >
            <Anchor className="w-4 h-4" />
            <span className="hidden sm:inline">Vessels</span>
            <span className="sm:hidden">Vessels</span>
          </button>
        )}
      </div>

      {/* ── Vessel count + AIS freshness badge ────────────────────────
          Replaces the bare "N vessels live" pill with a status-aware
          one. The dot colour and the "live" label honestly reflect the
          age of the most recent AIS frame:
            green + "live"      → < 10 min old
            cyan  + "5 min ago" → < 2 hours
            amber + "stale"     → between 2h and 6h (cron lagging)
            red   + "fixtures"  → mock fallback (cron down or first run) */}
      {(() => {
        const age = aisMeta?.newest_position_age_sec ?? null;
        const isMock = aisMeta?.source !== 'd1';
        const ageLabel = (() => {
          if (isMock) return 'sample data';
          if (age == null) return '—';
          if (age < 60) return 'live';
          if (age < 600) return `${Math.floor(age / 60)} min ago`;
          if (age < 7200) return `${Math.floor(age / 60)} min ago`;
          return `${Math.floor(age / 3600)} h ago`;
        })();
        const dotCls = (() => {
          if (isMock)        return 'bg-red-400';
          if (age == null)   return 'bg-slate-500';
          if (age < 600)     return 'bg-emerald-400 animate-pulse';
          if (age < 7200)    return 'bg-cyan-400';
          return 'bg-amber-400';
        })();
        const ringCls = (() => {
          if (isMock)        return 'border-red-500/40';
          if (age == null)   return 'border-slate-700/60';
          if (age < 7200)    return 'border-slate-700/60';
          return 'border-amber-500/40';
        })();
        return (
          <button
            type="button"
            onClick={() => setAisPanelOpen((v) => !v)}
            aria-expanded={aisPanelOpen}
            aria-label="Open the live AIS dashboard"
            className={`absolute bottom-8 left-4 z-20 flex items-center gap-2 bg-slate-900/85 backdrop-blur-md text-white text-sm pl-3 pr-2.5 py-1.5 rounded-full border ${ringCls} shadow-lg hover:bg-slate-900 hover:border-cyan-500/50 transition-colors active:scale-[0.98]`}
            title={
              isMock
                ? 'Showing sample fixtures because the live AIS feed is not configured or temporarily unavailable. Click for details.'
                : `Click for the live AIS dashboard · last frame ${aisMeta?.newest_position_age_sec != null ? `${aisMeta.newest_position_age_sec}s ago` : '—'}`
            }
          >
            <span className={`w-2 h-2 rounded-full ${dotCls}`} aria-hidden="true" />
            <span className="font-medium tabular-nums">{filteredShips.length}</span>
            <span className="text-slate-400 text-xs">vessels</span>
            <span className="text-slate-600 text-xs">·</span>
            <span className="text-slate-300 text-xs">AIS {ageLabel}</span>
            {/* Subtle clickability hint — small chevron, with a one-time
                gentle pulse the very first time the user lands so they
                discover the badge is interactive. After 6 s the pulse
                stops permanently (localStorage). */}
            <span
              className={`ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-cyan-300 ${
                hintShown ? '' : 'animate-pulse bg-cyan-500/20'
              }`}
              aria-hidden="true"
            >
              <ChevronUp
                className={`w-3 h-3 transition-transform ${aisPanelOpen ? 'rotate-180' : ''}`}
              />
            </span>
          </button>
        );
      })()}

      {/* ── Live AIS dashboard panel — opens above the badge on click ── */}
      {aisPanelOpen && (
        <LiveAISPanel
          ships={ships}
          filteredShips={filteredShips}
          newestAgeSec={aisMeta?.newest_position_age_sec ?? null}
          isLive={aisMeta?.source === 'd1'}
          onClose={() => setAisPanelOpen(false)}
        />
      )}

      {/* ── Zoom-aware tools (top-right, below the filter bar).
          Only appears when the inspector has zoomed into a port-scale
          view (zoom > 11). Two affordances:
            • 3D — switch to satellite + extruded buildings + tilted
              camera so the inspector gets a physical sense of the
              port before they travel.
            • Port info — opens the PortInfoPanel side drawer with
              place name, country, LOCODE guess, Wikipedia summary,
              outbound links, and public CCTV / weather pointers. */}
      <div className="absolute top-20 right-4 z-10 flex flex-col gap-2">
        {/* Environmental overlays — always available */}
        <button
          type="button"
          onClick={toggleRadar}
          title="Live precipitation radar (RainViewer)"
          aria-pressed={radarOn}
          className={`min-h-[40px] inline-flex items-center gap-1.5 px-3 rounded-lg backdrop-blur-md border text-xs font-semibold shadow-lg transition-all duration-150 active:scale-[0.98] ${
            radarOn
              ? 'bg-sky-500/20 border-sky-400 text-sky-100 shadow-sky-500/30'
              : 'bg-slate-900/85 border-slate-700 text-slate-200 hover:border-sky-500/50 hover:text-white'
          }`}
        >
          <CloudRain className="w-3.5 h-3.5" />
          <span>Radar</span>
        </button>
        <button
          type="button"
          onClick={toggleSeamarks}
          title="Nautical marks — buoys, lights, harbour marks (OpenSeaMap)"
          aria-pressed={seamarksOn}
          className={`min-h-[40px] inline-flex items-center gap-1.5 px-3 rounded-lg backdrop-blur-md border text-xs font-semibold shadow-lg transition-all duration-150 active:scale-[0.98] ${
            seamarksOn
              ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100 shadow-emerald-500/30'
              : 'bg-slate-900/85 border-slate-700 text-slate-200 hover:border-emerald-500/50 hover:text-white'
          }`}
        >
          <Anchor className="w-3.5 h-3.5" />
          <span>Seamarks</span>
        </button>
      </div>

      {currentZoom > 11 && (
        <div className="absolute top-44 right-4 z-10 flex flex-col gap-2">
          <button
            type="button"
            onClick={toggle3D}
            title={is3D ? 'Switch back to flat operational view' : 'See the port in 3D — satellite + buildings + terrain'}
            aria-pressed={is3D}
            className={`min-h-[40px] inline-flex items-center gap-1.5 px-3 rounded-lg backdrop-blur-md border text-xs font-semibold shadow-lg transition-all duration-150 active:scale-[0.98] ${
              is3D
                ? 'bg-cyan-500/20 border-cyan-400 text-cyan-100 shadow-cyan-500/30'
                : 'bg-slate-900/85 border-slate-700 text-slate-200 hover:border-cyan-500/50 hover:text-white'
            }`}
          >
            {is3D ? <MapIcon className="w-3.5 h-3.5" /> : <Box className="w-3.5 h-3.5" />}
            <span>{is3D ? 'Flat' : '3D'}</span>
          </button>
          <button
            type="button"
            onClick={openPortInfo}
            title="Open port + location intelligence (place, country, LOCODE, Wikipedia, public webcams, weather)"
            className="min-h-[40px] inline-flex items-center gap-1.5 px-3 rounded-lg backdrop-blur-md bg-slate-900/85 border border-slate-700 text-slate-200 hover:border-cyan-500/50 hover:text-white text-xs font-semibold shadow-lg transition-all duration-150 active:scale-[0.98]"
          >
            <Info className="w-3.5 h-3.5" />
            <span>Port info</span>
          </button>
        </div>
      )}

      {portInfoOpen && portInfoCenter && (
        <PortInfoPanel
          lng={portInfoCenter.lng}
          lat={portInfoCenter.lat}
          onClose={() => setPortInfoOpen(false)}
        />
      )}

      {/* ── Profile drawer ────────────────────────────────────────────────────── */}
      {profileOpen && profileId && (
        <VesselProfileDrawer
          vesselId={profileId}
          onClose={() => {
            setProfileOpen(false);
            setSelectedVessel(null);
          }}
        />
      )}
    </div>
  );
}
